import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import usePromptService from '../../services/promptService';
import PromptForm from './PromptForm';
import PromptHistoryModal from './PromptHistoryModal';
import ConfirmDialog from '../ConfirmDialog';

const PromptManagement = () => {
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const promptService = usePromptService();

  useEffect(() => {
    loadPrompts();
  }, [includeDeleted]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    filterPrompts();
  }, [prompts, searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPrompts = async () => {
    try {
      setLoading(true);
      const response = await promptService.getAllPrompts(includeDeleted);
      setPrompts(response.data || []);
    } catch (error) {
      toast.error('Failed to load prompts');
      console.error('Error loading prompts:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPrompts = () => {
    let filtered = prompts.filter(prompt => 
      prompt.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      prompt.description.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort by updated date (most recent first)
    filtered.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

    setFilteredPrompts(filtered);
  };

  const handleCreate = () => {
    setEditingPrompt(null);
    setShowForm(true);
  };

  const handleEdit = (prompt) => {
    setEditingPrompt(prompt);
    setShowForm(true);
  };

  const handleRequestDelete = (prompt) => {
    setPromptToDelete(prompt);
    setShowDeleteConfirm(true);
  };

  const handleDelete = async () => {
    if (!promptToDelete || deleteLoading) return;
    try {
      setDeleteLoading(true);
      await promptService.deletePrompt(promptToDelete.id);
      toast.success('Prompt deleted successfully');
      await loadPrompts();
    } catch (error) {
      toast.error('Failed to delete prompt');
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
      setPromptToDelete(null);
    }
  };

  const handleRestore = async (promptId) => {
    try {
      await promptService.restorePrompt(promptId);
      toast.success('Prompt restored successfully');
      await loadPrompts();
    } catch (error) {
      toast.error('Failed to restore prompt');
    }
  };

  const handleShowHistory = (prompt) => {
    setSelectedPrompt(prompt);
    setShowHistory(true);
  };

  const handleFormSubmit = async (formData) => {
    try {
      if (editingPrompt) {
        await promptService.updatePrompt(editingPrompt.id, formData);
        toast.success('Prompt updated successfully');
      } else {
        await promptService.createPrompt(formData);
        toast.success('Prompt created successfully');
      }
      setShowForm(false);
      setEditingPrompt(null);
      await loadPrompts();
    } catch (error) {
      toast.error(`Failed to ${editingPrompt ? 'update' : 'create'} prompt`);
    }
  };

  const handleInitializeDefaults = async () => {
   // if (!window.confirm('This will initialize default prompts. Continue?')) return;

    try {
      await promptService.initializeDefaultPrompts();
      toast.success('Default prompts initialized');
      await loadPrompts();
    } catch (error) {
      toast.error('Failed to initialize default prompts');
    }
  };

  const getStatusBadge = (prompt) => {
    if (prompt.deletedAt) {
      return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">Deleted</span>;
    }
    return null;
  };

  const getVersionBadge = (version) => (
    <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
      v{version}
    </span>
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-900">Prompt Management</h2>
          <p className="mt-1 text-sm text-gray-500">
            Manage AI prompts with version control and model selection
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleInitializeDefaults}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Initialize Defaults
          </button>
          <button
            onClick={handleCreate}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
          >
            Create Prompt
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search prompts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex items-center">
          <input
            id="include-deleted"
            type="checkbox"
            checked={includeDeleted}
            onChange={(e) => setIncludeDeleted(e.target.checked)}
            className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="include-deleted" className="text-sm text-gray-700">
            Include deleted
          </label>
        </div>
      </div>

      {filteredPrompts.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No prompts found</h3>
          <p className="text-gray-500 mb-4">Get started by creating your first prompt</p>
          <button
            onClick={handleCreate}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
          >
            Create Prompt
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="divide-y divide-gray-200">
            {filteredPrompts.map((prompt) => (
              <div key={prompt.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {prompt.name}
                      </p>
                      {getVersionBadge(prompt.version)}
                      {getStatusBadge(prompt)}
                    </div>
                    {prompt.description && (
                      <p className="text-sm text-gray-600 mb-2">{prompt.description}</p>
                    )}
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>Created: {new Date(prompt.createdAt).toLocaleString()}</div>
                      <div>Updated: {new Date(prompt.updatedAt).toLocaleString()}</div>
                      {prompt._count?.histories > 0 && (
                        <div>{prompt._count.histories} history entries</div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleShowHistory(prompt)}
                      className="p-2 text-gray-400 hover:text-gray-600"
                      title="View History"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleEdit(prompt)}
                      className="p-2 text-gray-400 hover:text-blue-600"
                      title="Edit"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    {prompt.deletedAt ? (
                      <button
                        onClick={() => handleRestore(prompt.id)}
                        className="p-2 text-gray-400 hover:text-green-600"
                        title="Restore"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                        </svg>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRequestDelete(prompt)}
                        className="p-2 text-gray-400 hover:text-red-600"
                        title="Delete"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <PromptForm
          prompt={editingPrompt}
          onSubmit={handleFormSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingPrompt(null);
          }}
        />
      )}

      {showHistory && selectedPrompt && (
        <PromptHistoryModal
          prompt={selectedPrompt}
          onClose={() => {
            setShowHistory(false);
            setSelectedPrompt(null);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPromptToDelete(null);
        }}
        onConfirm={handleDelete}
        title="Delete Prompt"
        message={`Are you sure you want to delete "${promptToDelete?.name || 'this prompt'}"? This is a critical action and will soft-delete the prompt.`}
        confirmText="Delete Prompt"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default PromptManagement;
